import { useEffect, useId, useRef, useState } from "react";
import { CoachModal } from "@/features/coach/components/CoachModal";
import { ChevronDown } from "lucide-react";
import { useLocation } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { getAdminTeams, getAppSettings, saveScope } from "@/lib/api";
import {
  publicAppSettingsKey,
  queryClient,
  unscopedKey,
} from "@/lib/queryClient";
import { useAuthStore } from "@/stores/auth";
import { useScopeStore } from "@/stores/scope";
import { TkticSelect } from "@/components/ui/TkticSelect";
import { TeamSwitcher } from "./TeamSwitcher";
import { hostKind } from "@/lib/hostKind";
import { ClubMark } from "@/components/ClubMark";
import { getUploadedClubIconUrl, getUploadedClubLogoUrl } from "@/lib/matchLogos";

import {
  impliedTeamSeason,
  isArchivedTeam,
  lineageKeyOf,
  newestSeasonFirst,
  teamSeason,
} from "./seasonGroups";
import { useScopeSelection } from "./useScopeSelection";

export function isCoachingWorkspacePath(pathname: string): boolean {
  return /^\/coach(?:\/|$)/.test(pathname);
}

export function isRecipientWorkspacePath(pathname: string): boolean {
  return /^\/feedback(?:\/|$)/.test(pathname);
}

/** Authoritative scope lives in the existing store and server; no Apply buffer. */
export function GlobalScopeBar() {
  const { pathname } = useLocation();
  const settings = useQuery({
    queryKey: publicAppSettingsKey(),
    queryFn: getAppSettings,
    staleTime: 60_000,
  });
  const me = useAuthStore((s) => s.me);
  const active = useScopeStore((s) => s.active);
  const mutation = useScopeSelection();
  const recipient = isRecipientWorkspacePath(pathname);
  const [open, setOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const popupId = useId();
  const clubName =
    settings.data?.settings?.club_title ||
    me?.current_club?.name ||
    "Your club";
  const clubIconUrl = getUploadedClubIconUrl(settings.data);
  const clubLogoUrl = getUploadedClubLogoUrl(settings.data);
  useEffect(() => {
    setOpen(false);
  }, [pathname]);
  // A seasoned team implies its season (#908 D1): the separate season
  // control is only for legacy teams. `active_scope.team` carries no
  // season, so the membership entry in `teams[]` is the source; a global
  // admin's non-membership team reads its season from the same
  // `admin-teams-switcher` rows the TeamSwitcher lists.
  const isGlobalAdmin = !recipient && !!me?.user?.is_global_admin;
  const activeIsMembership =
    !!active?.team && !!me?.teams.some((t) => t.id === active.team.id);
  const adminTeamsKey = unscopedKey("admin-teams-switcher");
  // A global admin also needs the rows for the Season control (the active
  // team's other season teams may be non-membership teams), but only once
  // the Team & season dialog is open; on a membership team the closed bar
  // needs none.
  const adminTeams = useQuery({
    queryKey: adminTeamsKey,
    queryFn: getAdminTeams,
    enabled:
      isGlobalAdmin &&
      !!active?.team &&
      (!activeIsMembership || open) &&
      hostKind() === "club" &&
      isCoachingWorkspacePath(pathname),
    staleTime: 60_000,
  });
  const seasons = me?.seasons ?? [];
  // #908 D1: a team is formed per season and Carry forward links its season
  // teams, so the Season control lists the active team's seasons and moves
  // between those teams. Only teams the user can pick: memberships, plus
  // the admin rows for a global admin.
  const adminRows = Array.isArray(adminTeams.data) ? adminTeams.data : [];
  const seasonName = new Map(seasons.map((s) => [s.id, s.name]));
  const pickable = [
    ...(me?.teams ?? []).map((t) => ({
      id: t.id,
      key: lineageKeyOf(t),
      season: teamSeason(t),
      archived: isArchivedTeam(t),
    })),
    ...(isGlobalAdmin
      ? adminRows
          .filter((row) => !me?.teams.some((t) => t.id === row.id))
          .map((row) => ({
            id: row.id,
            key: lineageKeyOf(row),
            season: row.season_id
              ? { id: row.season_id, name: seasonName.get(row.season_id) ?? row.season_id }
              : null,
            archived: isArchivedTeam(row),
          }))
      : []),
  ];
  const activeKey = pickable.find((t) => t.id === active?.team?.id)?.key;
  const seasonTeams = activeKey
    ? newestSeasonFirst(pickable.filter((t) => t.key === activeKey), seasons)
    : [];
  const impliedLookup = impliedTeamSeason(
    active?.team?.id,
    me?.teams,
    Array.isArray(adminTeams.data) ? adminTeams.data : undefined,
    seasons,
  );
  const impliedSeason = impliedLookup ?? null;
  // A global admin's non-membership team whose admin row is still loading:
  // neither the implied season nor the free control yet.
  const seasonUnknown =
    impliedLookup === undefined && isGlobalAdmin && !activeIsMembership && adminTeams.isLoading;
  // The backend rejects (409) a scope that pairs a seasoned team with a
  // different season. Re-select the team's own season instead — from a
  // fresh admin-teams read for a global admin (the cached row may predate
  // the team getting its season).
  const selectScope = async (
    selection: Parameters<typeof saveScope>[0],
  ): Promise<boolean> => {
    if (await mutation.select(selection)) return true;
    if (mutation.lastFailureStatus() !== 409) return false;
    let rows = queryClient.getQueryData<Awaited<ReturnType<typeof getAdminTeams>>>(adminTeamsKey);
    if (isGlobalAdmin) {
      try {
        rows = await queryClient.fetchQuery({
          queryKey: adminTeamsKey,
          queryFn: getAdminTeams,
          staleTime: 0,
        });
      } catch {
        // Fall back to whatever rows are cached.
      }
    }
    const season = impliedTeamSeason(selection.team_id, me?.teams, rows, seasons);
    if (!season || season.id === selection.season_id) return false;
    return mutation.select({ team_id: selection.team_id, season_id: season.id });
  };
  const selection = { ...mutation, select: selectScope };
  // A saved scope that pairs a seasoned team with another season (saved
  // before the team got its season, or picked through the old free
  // control) is corrected once per mismatch; a failed correction is not
  // retried on every render.
  const corrected = useRef<string | null>(null);
  useEffect(() => {
    if (!me?.user || !active?.team || !impliedSeason) return;
    if (active.season?.id === impliedSeason.id) return;
    const attempt = `${active.team.id}:${impliedSeason.id}`;
    if (corrected.current === attempt) return;
    corrected.current = attempt;
    void mutation.select({ team_id: active.team.id, season_id: impliedSeason.id });
  }, [me?.user, active, impliedSeason?.id, mutation]);
  // Coaching and recipient workspaces share the saved scope. Public team pages
  // carry their own URL-backed season selector, while club administration
  // owns its context inside AdminShell; rendering this bar on either surface
  // creates a second, conflicting source of navigation state.
  if (hostKind() !== "club" || (!isCoachingWorkspacePath(pathname) && !recipient))
    return null;
  if (!me?.user) return null;
  return (
    <section
      aria-label="Team and season"
      className="global-scope-bar coach-workspace-scope"
      data-testid="global-scope-bar"
    >
      <div className="global-scope-control">
        <button
          ref={trigger}
          type="button"
          aria-label="Choose team and season"
          aria-expanded={open}
          aria-controls={popupId}
          onClick={() => setOpen((v) => !v)}
          className="global-scope-trigger"
        >
          <ClubMark
            name={clubName}
            imageUrl={clubIconUrl}
            fallbackImageUrl={clubLogoUrl}
            className="global-scope-club-mark"
          />
          <span className="coach-workspace-scope-copy">
            <span>{active?.team.name ?? "Choose team"}</span>
            <span>{active?.season?.name ?? (active?.team ? "No season" : "Choose season")}</span>
          </span>
          <ChevronDown className="size-4 shrink-0" aria-hidden />
        </button>
        {open ? (
          <CoachModal
            title="Team & season"
            closeLabel="Close team and season"
            description="Choose the context for your workspace."
            onClose={() => setOpen(false)}
            returnFocusTo={trigger.current}
          >
            <p className="scope-role mb-3 text-xs text-ink-muted">
              {(me.is_club_admin
                ? "Club administrator"
                : (me.teams.find((t) => t.id === active?.team.id)?.membership_role ?? "")
              ).replace(/_/g, " ")}
            </p>
            <div
              id={popupId}
              className="global-scope-fields"
              role="group"
              aria-label="Team and season selection"
            >
              <div>
                <p className="mb-1 text-xs text-ink-muted">Team</p>
                <TeamSwitcher showSingleTeam recipient={recipient} selection={selection} />
              </div>
              {active?.team && seasonTeams.length > 1 ? (
                <div className="grid gap-3">
                  <div>
                    <p className="mb-1 text-xs text-ink-muted">Season</p>
                    <TkticSelect
                      ariaLabel="Season"
                      data-testid="global-scope-team-season"
                      value={active.team.id}
                      options={seasonTeams.map((t) => ({
                        value: t.id,
                        label: `${t.season?.name ?? "Before seasons"}${t.archived ? " · archived" : ""}`,
                      }))}
                      disabled={mutation.isPending}
                      onChange={(teamId) => {
                        const choice = seasonTeams.find((t) => t.id === teamId);
                        if (!choice) return;
                        void selectScope(
                          choice.season
                            ? { team_id: choice.id, season_id: choice.season.id }
                            : { team_id: choice.id },
                        );
                      }}
                    />
                  </div>
                  {impliedSeason === null && me.seasons.length > 0 ? (
                    // The team from before seasons keeps its free season
                    // choice for its older matches and feedback.
                    <div>
                      <p className="mb-1 text-xs text-ink-muted">Earlier season</p>
                      <TkticSelect
                        ariaLabel="Earlier season"
                        value={active.season?.id ?? ""}
                        options={me.seasons.map((season) => ({
                          value: season.id,
                          label: season.name,
                        }))}
                        disabled={mutation.isPending}
                        onChange={(seasonId) =>
                          void selectScope({
                            team_id: active.team.id,
                            season_id: seasonId,
                          })
                        }
                      />
                    </div>
                  ) : null}
                </div>
              ) : active?.team && impliedSeason ? (
                <div>
                  <p className="mb-1 text-xs text-ink-muted">Season</p>
                  <p
                    data-testid="global-scope-season-implied"
                    className="text-sm text-ink"
                  >
                    {impliedSeason.name}
                  </p>
                  <p className="text-xs text-ink-muted">
                    Set by the team.
                  </p>
                </div>
              ) : active?.team && seasonUnknown ? (
                <p className="text-sm text-ink-muted">Loading season…</p>
              ) : active?.team && me.seasons.length > 0 ? (
                <div>
                  <p className="mb-1 text-xs text-ink-muted">Season</p>
                  <TkticSelect
                    ariaLabel="Season"
                    value={active.season?.id ?? ""}
                    options={me.seasons.map((season) => ({
                      value: season.id,
                      label: season.name,
                    }))}
                    disabled={mutation.isPending}
                    onChange={(seasonId) =>
                      void selectScope({
                        team_id: active.team.id,
                        season_id: seasonId,
                      })
                    }
                  />
                </div>
              ) : (
                <p className="text-sm text-ink-muted">
                  {active?.team ? "No seasons available." : "Choose a team to see its seasons."}
                </p>
              )}
              {mutation.error ? (
                <p role="alert" className="text-sm text-destructive">
                  {mutation.error}
                </p>
              ) : null}
            </div>
          </CoachModal>
        ) : null}
      </div>
    </section>
  );
}
